import { Check } from 'lucide-react';

export type ClientOnboardingWizardStepperProps = {
  labels: readonly string[];
  currentStep: number;
  maxReached: number;
  onSelectStep: (step: number) => void;
  navigationAriaLabel?: string;
};

/** Brand purple */
const P = '#8B80F9';
const P_DEPTH = '#7B6EE8';

export function ClientOnboardingWizardStepper({
  labels,
  currentStep,
  maxReached,
  onSelectStep,
  navigationAriaLabel = 'Onboarding steps',
}: ClientOnboardingWizardStepperProps) {
  const last = labels.length - 1;

  return (
    <nav aria-label={navigationAriaLabel} className="w-full">
      {/* ── Label row ────────────────────────────────────────────── */}
      <div className="flex items-end mb-1.5">
        {labels.map((label, i) => {
          const n = i + 1;
          const done = n < currentStep;
          const active = n === currentStep;
          const cleanLabel = label.replace(/^\[|\]$/g, '');

          const labelColor = active ? P : done ? P_DEPTH : 'var(--vscode-text-tertiary)';
          const labelWeight = active ? 700 : done ? 600 : 400;

          return (
            <div
              key={label}
              className="flex flex-col items-start"
              style={{ flex: i === 0 ? '0 0 auto' : i === last ? '0 0 auto' : '1 1 0' }}
            >
              <span
                className="text-[10.5px] leading-tight transition-colors mb-0"
                style={{
                  fontWeight: labelWeight,
                  color: labelColor,
                  whiteSpace: 'nowrap',
                  letterSpacing: active ? '0.01em' : undefined,
                  /* shift label right by half-node width so it sits above node centre */
                  paddingLeft: i === 0 ? 0 : 14,
                }}
              >
                {cleanLabel}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Node + connector row ─────────────────────────────────── */}
      <div className="flex items-center w-full">
        {labels.map((label, i) => {
          const n = i + 1;
          const done = n < currentStep;
          const active = n === currentStep;
          const reachable = n <= maxReached;

          /* connector after this node (not for the last step) */
          const connectorDone = n < currentStep;
          const connectorColor = connectorDone
            ? P
            : 'color-mix(in srgb, var(--vscode-border-primary) 45%, transparent)';

          /* node colours */
          const nodeStyle: React.CSSProperties = done
            ? {
                background: P,
                border: `2px solid ${P}`,
                color: '#fff',
                boxShadow: `0 2px 8px rgba(139,128,249,0.35)`,
              }
            : active
              ? {
                  background: 'transparent',
                  border: `2px solid ${P}`,
                  color: P,
                }
              : {
                  background: 'transparent',
                  border: `2px solid color-mix(in srgb, var(--vscode-border-primary) 50%, transparent)`,
                  color: 'var(--vscode-text-tertiary)',
                  opacity: reachable ? 1 : 0.55,
                };

          return (
            <div key={label} className="flex items-center" style={{ flex: i === last ? '0 0 auto' : '1 1 0' }}>

              {/* Node */}
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && onSelectStep(n)}
                aria-current={active ? 'step' : undefined}
                className="shrink-0 focus:outline-none focus-visible:ring-2 rounded-full transition-all duration-200"
                style={{ cursor: reachable ? 'pointer' : 'default' }}
              >
                <span
                  className="flex items-center justify-center rounded-full transition-all duration-200"
                  style={{ width: 28, height: 28, ...nodeStyle }}
                >
                  {done ? (
                    /* checkmark — matches the SVG in the reference exactly */
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  ) : active ? (
                    /* filled inner dot = current */
                    <span
                      className="rounded-full"
                      style={{ width: 10, height: 10, background: P }}
                    />
                  ) : (
                    <span className="text-[11px] font-semibold tabular-nums leading-none">{n}</span>
                  )}
                </span>
              </button>

              {/* Connector line (not after the last node) */}
              {i < last && (
                <div
                  className="flex-1 transition-colors duration-300"
                  style={{
                    height: 3,
                    borderRadius: 2,
                    background: connectorColor,
                    marginLeft: -1,
                    marginRight: -1,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
